// /resources/js/Stores/CatalogStore.js
import axios from 'axios';
import {defineStore} from 'pinia';
import {usePage} from '@inertiajs/vue3';
import {nextTick} from 'vue';
import {numberCommaFormat, checkDocument, getLocalStorage, setLocalStorage} from '@js/helpers.ts';

const orderTypes = {
    default: {type: 'default', title: 'По популярности'},
    asc: {type: 'asc', title: 'Сначала дешевле'},
    desc: {type: 'desc', title: 'Сначала дороже'},
};

const perPageArray = [15, 30, 50, 100];

const defaultFilterData = {
    price: [0, 0],
    per_page: 15,
    sort: orderTypes.default.type,
};

const CATALOG_SEGMENT = 'catalog';

const gridMods = {
    wide: 'wide',
    small: 'small',
};

export const useCatalogStore = defineStore('catalogStore', {
    state: () => ({
        grid_mods: gridMods,
        grid_mode: gridMods.wide,

        // Категория читаем из URL-пути: /catalog/:category
        category: null,

        is_filter_open: false,

        loadings: {
            filter_first_load: false,
            catalog_loaded: false,
            filter_limits_loaded: false,
        },

        total_result: 0,
        order_types: orderTypes,
        per_page_array: perPageArray,

        // Данные для построения фильтров (фасеты)
        filter_data: {
            limits: {
                price: [0, 0],
            },
            categories: [],
            sub_categories: [],
            characteristics: [], // [{type_id, type_name, values:[{value,count}]}]
        },

        // Выбранные значения фильтров
        filter: JSON.parse(JSON.stringify(defaultFilterData)),
        selected_sub_categories: [],   // ["Арматура рифленая", ...] (id=title)
        selected_characteristics: {},  // { [type_id]: ["значение1","значение2"] }

        pagination: {
            current_page: 1,
            total_pages: 1,
        },

        timeout_events: {
            filter: undefined,
        },

        products: [],
        selected_filters: [],
        search_query: null,
        savedScroll: 0,

        // чтобы не биндать popstate много раз
        popstateBound: false,
    }),
    actions: {
        // ===================== helpers: путь/URL =====================
        readCategoryFromPath() {
            // Возвращает сегмент после /catalog — /catalog/:category
            let pathname = '';
            try {
                const page = usePage();
                pathname = (page?.url?.split('?')[0]) || '/';
            } catch {
                pathname = window.location.pathname || '/';
            }
            const parts = pathname.split('/').filter(Boolean);
            const idx = parts.indexOf(CATALOG_SEGMENT);
            const raw = (idx !== -1 && parts[idx + 1]) ? parts[idx + 1] : null;
            return raw ? decodeURIComponent(raw) : null;
        },

        buildPathWithCategory() {
            const cat = this.category ? encodeURIComponent(this.category) : null;
            return `/${CATALOG_SEGMENT}${cat ? `/${cat}` : ''}`;
        },

        // ===================== UI misc =====================
        toggleFilter() {
            this.is_filter_open = !this.is_filter_open;
        },
        setSavedScroll(y) {
            this.savedScroll = y;
        },

        // ===================== Навигация/страница =====================
        onPageChange(page) {
            this.pagination.current_page = page;
            this.getCatalog();
            this.addParamsToUrl();
            if (checkDocument()) {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }
        },

        changeOrder(type) {
            this.filter.sort = type;
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        changePerPage(per_page) {
            this.filter.per_page = per_page;
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        // ===================== URL helpers =====================
        hasUrlParams() {
            try {
                const page = usePage();
                const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
                return queryParams.toString().length > 0;
            } catch {
                const queryParams = new URLSearchParams(window.location.search || '');
                return queryParams.toString().length > 0;
            }
        },

        syncUrlParamsWithState() {
            // 1) категория — ТОЛЬКО из пути
            this.category = this.readCategoryFromPath();

            // 2) остальное — из query
            let queryParams;
            try {
                const page = usePage();
                queryParams = new URLSearchParams(page.url.split('?')[1] || '');
            } catch {
                queryParams = new URLSearchParams(window.location.search || '');
            }

            const getJson = (key, fallback) => {
                try {
                    return queryParams.has(key) ? JSON.parse(queryParams.get(key)) : fallback;
                } catch {
                    return fallback;
                }
            };

            // sub_categories как JSON-массив
            this.selected_sub_categories = getJson('sub_categories', []);

            // characteristics как JSON-массив [{type_id, values:[]}]
            const chFromUrl = getJson('characteristics', []);
            if (Array.isArray(chFromUrl)) {
                const obj = {};
                chFromUrl.forEach((c) => {
                    if (c && typeof c.type_id !== 'undefined' && Array.isArray(c.values)) {
                        obj[c.type_id] = c.values;
                    }
                });
                this.selected_characteristics = obj;
            }

            // per_page
            const per = queryParams.has('per_page') ? Number(queryParams.get('per_page')) : defaultFilterData.per_page;
            this.filter.per_page = perPageArray.includes(per) ? per : defaultFilterData.per_page;

            // sort
            this.filter.sort = queryParams.has('sort') ? queryParams.get('sort') : defaultFilterData.sort;

            // search
            this.search_query = queryParams.has('search') ? queryParams.get('search') : null;

            // page
            this.pagination.current_page = queryParams.has('page') ? parseInt(queryParams.get('page')) : 1;

            // price
            const pf = queryParams.has('price_from') ? Number(queryParams.get('price_from')) : null;
            const pt = queryParams.has('price_to') ? Number(queryParams.get('price_to')) : null;
            if (pf !== null || pt !== null) {
                const cur = [...this.filter.price];
                if (pf !== null && !Number.isNaN(pf)) cur[0] = pf;
                if (pt !== null && !Number.isNaN(pt)) cur[1] = pt;
                this.filter.price = cur;
            }
        },

        addParamsToUrl() {
            if (!history.replaceState) return;

            const params = new URLSearchParams();

            // category — ТОЛЬКО в path, НЕ в query
            if (this.search_query) params.set('search', this.search_query);

            // price (как отдельные поля)
            if (Array.isArray(this.filter.price)) {
                params.set('price_from', this.filter.price?.[0] ?? '');
                params.set('price_to', this.filter.price?.[1] ?? '');
            }

            // sub_categories
            if (this.selected_sub_categories?.length) {
                params.set('sub_categories', JSON.stringify(this.selected_sub_categories));
            }

            // characteristics
            const chPayload = this.buildCharacteristicsPayload();
            if (chPayload.length) {
                params.set('characteristics', JSON.stringify(chPayload));
            }

            params.set('sort', this.filter.sort);
            params.set('page', this.pagination.current_page);
            params.set('per_page', this.filter.per_page);

            // Чистим пустые значения (красивый URL)
            for (const [k, v] of [...params.entries()]) {
                if (v === '' || v === 'null' || v === '[]') params.delete(k);
            }

            const basePath = this.buildPathWithCategory();
            const qs = params.toString();
            const newurl = `${window.location.protocol}//${window.location.host}${basePath}${qs ? `?${qs}` : ''}`;
            window.history.replaceState({path: newurl}, '', newurl);
        },

        getParamFromUrl(param, defaultValue) {
            try {
                const page = usePage();
                const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
                return queryParams.has(param) ? parseFloat(queryParams.get(param)) : defaultValue;
            } catch {
                const queryParams = new URLSearchParams(window.location.search || '');
                return queryParams.has(param) ? parseFloat(queryParams.get(param)) : defaultValue;
            }
        },

        // ===================== Builders =====================
        buildCharacteristicsPayload() {
            const out = [];
            Object.entries(this.selected_characteristics || {}).forEach(([typeId, values]) => {
                if (Array.isArray(values) && values.length) {
                    out.push({type_id: Number(typeId), values});
                }
            });
            return out;
        },

        checkRangeBeforeSend(type, checkType) {
            const idx = checkType === 'max' ? 1 : 0;
            const limit = this.filter_data.limits?.[type]?.[idx];
            const val = this.filter?.[type]?.[idx];
            if (typeof limit === 'undefined' || typeof val === 'undefined') return null;
            return Number(limit) !== Number(val) ? val : null;
        },

        // ===================== API =====================
        async getFilterData() {
            this.loadings.catalog_loaded = false;

            const params = {
                // ВАЖНО: для API категорию передаем параметром (slug/title — как на бэке ожидается)
                category: this.category || null,
                sub_categories: this.selected_sub_categories?.length ? this.selected_sub_categories : null,
                search: this.search_query || null,
                price_from: this.checkRangeBeforeSend('price', 'min'),
                price_to: this.checkRangeBeforeSend('price', 'max'),
                characteristics: this.buildCharacteristicsPayload(),
                sort: this.filter.sort,
                debug: 0,
            };

            try {
                const {data} = await axios.get('/axios/catalog/get-filter-data', {params});

                // лимиты
                const minP = data?.limits?.min_price ?? 0;
                const maxP = data?.limits?.max_price ?? 0;

                if (!this.loadings.filter_limits_loaded) {
                    this.filter_data.limits.price = [minP, maxP];

                    // если в URL не было цены — выставим полные лимиты
                    const pf = this.getParamFromUrl('price_from', null);
                    const pt = this.getParamFromUrl('price_to', null);
                    this.filter.price = [
                        pf !== null && !Number.isNaN(pf) ? pf : minP,
                        pt !== null && !Number.isNaN(pt) ? pt : maxP,
                    ];
                }
                // else {
                //     this.filter_data.limits.price = [minP, maxP];
                // }

                // списки
                this.filter_data.categories = data?.filter_data?.categories ?? [];
                this.filter_data.sub_categories = data?.filter_data?.sub_categories ?? [];
                this.filter_data.characteristics = data?.filter_data?.characteristics ?? [];

                this.loadings.filter_limits_loaded = true;
                this.loadings.filter_first_load = true;

                this.updateSelectedFilters();
                this.addParamsToUrl();
            } catch (e) {
                console.error('getFilterData error:', e?.message || e);
                this.loadings.filter_limits_loaded = false;
            }
        },

        async getCatalog() {
            const params = {
                category: this.category || null,
                sub_categories: this.selected_sub_categories?.length ? this.selected_sub_categories : null,
                search: this.search_query || null,
                price_from: this.checkRangeBeforeSend('price', 'min'),
                price_to: this.checkRangeBeforeSend('price', 'max'),
                characteristics: this.buildCharacteristicsPayload(),
                sort: this.filter.sort,
                page: this.pagination.current_page,
                per_page: this.filter.per_page,
            };

            try {
                const {data} = await axios.get('/axios/catalog', {params});
                this.pagination.total_pages = data?.total_pages ?? 1;
                this.total_result = data?.total ?? 0;
                this.products = data?.products ?? [];
                this.loadings.catalog_loaded = true;
                this.updateSelectedFilters();
                this.addParamsToUrl();
            } catch (e) {
                console.error('getCatalog error:', e?.message || e);
                this.loadings.catalog_loaded = false;
            }
        },

        // ===================== Выбор фильтров =====================
        toggleSubCategory(title) {
            if (!title) return;
            const set = new Set(this.selected_sub_categories);
            set.has(title) ? set.delete(title) : set.add(title);
            this.selected_sub_categories = Array.from(set);
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        isChecked(type_id, value) {
            const arr = this.selected_characteristics?.[type_id] || [];
            return arr.includes(value);
        },

        toggleCharacteristic(type_id, value) {
            if (typeof type_id === 'undefined' || value === undefined || value === null) return;
            const current = this.selected_characteristics?.[type_id] ? [...this.selected_characteristics[type_id]] : [];
            const idx = current.indexOf(value);
            if (idx === -1) current.push(value);
            else current.splice(idx, 1);
            this.selected_characteristics = {
                ...this.selected_characteristics,
                [type_id]: current,
            };
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        applyFiltersDebounced(delay = 350) {
            if (this.timeout_events.filter) clearTimeout(this.timeout_events.filter);
            this.timeout_events.filter = setTimeout(async () => {
                await this.getFilterData(); // сначала фасеты/лимиты с учётом выбранных фильтров
                await nextTick();
                await this.getCatalog();    // затем товары
            }, delay);
        },

        // ===================== Выбранные «чипы» (UI) =====================
        updateSelectedFilters() {
            const selected = [];

            // Подкатегории
            (this.selected_sub_categories || []).forEach((t) => {
                selected.push({
                    label: 'Подкатегория',
                    title: t,
                    value: t,
                    field: 'sub_category',
                });
            });

            // Цена
            const [min, max] = this.filter.price || [0, 0];
            const [limitMin, limitMax] = this.filter_data.limits?.price || [0, 0];
            if (min > limitMin || max < limitMax) {
                selected.push({
                    label: `Цена за метр`,
                    title: `${numberCommaFormat(min)} - ${numberCommaFormat(max)} ₽`,
                    value: `${min}_${max}`,
                    field: 'price',
                });
            }

            // Характеристики
            const typesById = {};
            (this.filter_data.characteristics || []).forEach((g) => {
                typesById[g.type_id] = g;
            });

            Object.entries(this.selected_characteristics || {}).forEach(([typeId, values]) => {
                const group = typesById[typeId];
                const label = group?.type_name || `Хар-ка ${typeId}`;
                (values || []).forEach((val) => {
                    selected.push({
                        label,
                        title: val,
                        value: val,
                        field: 'characteristics',
                        type_id: Number(typeId),
                    });
                });
            });

            this.selected_filters = selected;
        },

        removeFilter(tag) {
            if (!tag?.field) return;

            if (tag.field === 'price') {
                const [limitMin, limitMax] = this.filter_data.limits?.price || [0, 0];
                this.filter.price = [limitMin, limitMax];
            } else if (tag.field === 'sub_category') {
                this.selected_sub_categories = (this.selected_sub_categories || []).filter((v) => v !== tag.value);
            } else if (tag.field === 'characteristics' && tag.type_id) {
                const arr = (this.selected_characteristics?.[tag.type_id] || []).filter((v) => v !== tag.value);
                this.selected_characteristics = {...this.selected_characteristics, [tag.type_id]: arr};
            }

            this.pagination.current_page = 1;
            this.updateSelectedFilters();
            this.applyFiltersDebounced();
        },

        // ===================== Сброс =====================
        resetFilter() {
            const keepCategory = this.readCategoryFromPath(); // всегда из пути
            const keepSearch = this.search_query;

            this.loadings.catalog_loaded = false;
            this.loadings.filter_first_load = false;
            this.loadings.filter_limits_loaded = false;

            this.pagination.current_page = 1;

            this.products = [];
            this.filter_data = {
                limits: {price: [0, 0]},
                categories: [],
                sub_categories: [],
                characteristics: [],
            };

            this.filter = JSON.parse(JSON.stringify(defaultFilterData));
            this.selected_sub_categories = [];
            this.selected_characteristics = {};
            this.selected_filters = [];

            this.category = keepCategory || null;
            this.search_query = keepSearch || null;

            this.addParamsToUrl();
            // this.getFilterData();
        },

        // ===================== Инициализация =====================
        async initFromUrl() {
            // дерни это в mounted страницы каталога
            this.resetFilter();
            this.syncUrlParamsWithState();
            await this.getFilterData();
            await this.getCatalog();

            this.is_filter_watcher_on = true;

            if (!this.popstateBound && checkDocument()) {
                window.addEventListener('popstate', () => {
                    this.syncUrlParamsWithState();
                    this.applyFiltersDebounced(0);
                });
                this.popstateBound = true;
            }
        },

        setGridMode(mode) {
            this.grid_mode = mode;
            setLocalStorage('grid_mode', mode, 43200)
        }
    },
});
